/**
 * Una campaña de mail (`/activa/emails/campanas/:id`), en tres pestañas (`?vista=`, así un enlace abre la misma):
 * - «Cómo le fue»: los números, el embudo, por día, por asunto (cada paso de la secuencia) y por hora;
 * - «Personas»: la base de esa campaña, con filtros y el CSV;
 * - «Respuestas»: lo que contestaron.
 *
 * Arriba de las pestañas, lo que hay que hacer con ella: quienes pidieron la baja contestando y siguen recibiendo, y lo
 * que hay que revisar. Las pestañas cumplen el contrato de `role="tablist"` (flechas, Home/End y roving tabindex), como
 * `CampanaOficialDetallePage`. Una campaña de otro comercio es un 404, igual que una que no existe.
 */

import type { KeyboardEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { emailsApi } from "../api";
import type { DetalleCampana } from "../types";
import { usePedido } from "../hooks/usePedido";
import { useEstadoEmails } from "../contexto";
import { Tarjeta } from "../../wa-oficial/components/Tarjeta";
import { AnalisisEmails } from "../components/AnalisisEmails";
import { Esqueleto, ErrorTarjeta, Vacio } from "../../wa-oficial/components/estadisticas/comun";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { num, plural } from "../../campana-nueva/utils/format";
import { Embudo, Indicadores } from "../components/Indicadores";
import { AvisoBajas } from "../components/AvisoBajas";
import { Alertas } from "../components/Alertas";
import { GraficoDiario, GraficoHoras } from "../components/Graficos";
import { TablaPersonas } from "../components/TablaPersonas";
import { ListaRespuestas } from "../components/ListaRespuestas";
import { ChipEstadoCampana } from "../components/Chips";
import { ErrorRefresco } from "../components/ErrorRefresco";
import { R_EMAILS, asuntoLegible, conDatos, diaMesHora, tasaTexto, textoSalida, textoSeguimiento } from "../logic/emails";

type Vista = "estadisticas" | "personas" | "respuestas";
const VISTAS: { valor: Vista; texto: string }[] = [
  { valor: "estadisticas", texto: "Cómo le fue" },
  { valor: "personas", texto: "Personas" },
  { valor: "respuestas", texto: "Respuestas" },
];

function Pestanas({ vista, onElegir }: { vista: Vista; onElegir: (v: Vista) => void }) {
  const onKeyDown = (e: KeyboardEvent) => {
    const i = VISTAS.findIndex((v) => v.valor === vista);
    let sig = -1;
    if (e.key === "ArrowRight") sig = (i + 1) % VISTAS.length;
    else if (e.key === "ArrowLeft") sig = (i - 1 + VISTAS.length) % VISTAS.length;
    else if (e.key === "Home") sig = 0;
    else if (e.key === "End") sig = VISTAS.length - 1;
    if (sig < 0) return;
    e.preventDefault();
    onElegir(VISTAS[sig].valor);
    document.getElementById(`tab-email-${VISTAS[sig].valor}`)?.focus();
  };
  return (
    <div role="tablist" aria-label="Qué ver de la campaña" onKeyDown={onKeyDown}
      className="inline-flex max-w-full items-center gap-1 rounded-xl bg-slate-100 p-1 overflow-x-auto">
      {VISTAS.map((v) => {
        const activa = vista === v.valor;
        return (
          <button key={v.valor} id={`tab-email-${v.valor}`} type="button" role="tab" aria-selected={activa} aria-controls={`panel-email-${v.valor}`}
            tabIndex={activa ? 0 : -1} onClick={() => onElegir(v.valor)}
            className={`h-8 px-3 rounded-lg text-sm font-semibold whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 ${activa ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {v.texto}
          </button>
        );
      })}
    </div>
  );
}

export default function CampanaEmailPage() {
  const { id = "" } = useParams();
  const [sp, setSp] = useSearchParams();
  const vistaUrl = sp.get("vista");
  const vista: Vista = VISTAS.some((v) => v.valor === vistaUrl) ? (vistaUrl as Vista) : "estadisticas";
  const { version } = useEstadoEmails();
  const equipo = useEsEquipoDeenex();
  const r = usePedido<DetalleCampana>(() => emailsApi.campana(id), id, { refresco: version });
  const c = r.dato;

  // Cambiar de pestaña limpia los filtros de la otra (cada una tiene los suyos en la URL).
  const irA = (v: Vista) => setSp(v === "estadisticas" ? new URLSearchParams() : new URLSearchParams({ vista: v }), { replace: true });

  if (!c) {
    return (
      <div className="space-y-4">
        <Link to={`${R_EMAILS}/campanas`} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900"><ArrowLeft size={15} /> Campañas</Link>
        {r.error
          ? <ErrorTarjeta texto={r.code === "NOT_FOUND" ? "No encontramos esta campaña en este comercio." : `No pudimos traer la campaña. ${r.error}`} onReintentar={r.code === "NOT_FOUND" ? undefined : r.recargar} />
          : <Esqueleto alto="h-64" etiqueta="Cargando la campaña" />}
      </div>
    );
  }

  const seguimiento = textoSeguimiento(c.seguimiento);
  const k = c.conteos;
  const rutaCampana = `${R_EMAILS}/campanas/${encodeURIComponent(String(c.id))}`;
  // La falta de baja en un clic ya la dice la alerta mientras la campaña manda; si no, una nota.
  const alertaBaja = c.alertas.some((a) => a.codigo === "sin_baja_un_clic");

  return (
    <div className="space-y-5">
      <Link to={`${R_EMAILS}/campanas`} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-slate-900"><ArrowLeft size={15} /> Campañas</Link>
      <header className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h2 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight break-words">{c.titulo || `Campaña ${c.id}`}</h2>
          <ChipEstadoCampana estado={c.estado} faltan={c.progreso?.faltan} />
          {!c.enSnov && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Ya no está en Snov</span>}
        </div>
        <p className="text-sm text-slate-500 break-words">
          {textoSalida(c.primerEnvio, c.ultimoEnvio)}
          {c.remitentes.length > 0 && <> · desde {c.remitentes.map((x) => x.email).join(", ")}</>}
          {c.pasos > 1 && <> · {num(c.pasos)} pasos</>}
          {c.estado === "activa" && c.progreso && c.progreso.faltan > 0 && (
            <> · faltan {num(c.progreso.faltan)} {plural(c.progreso.faltan, "persona", "personas")}{c.progreso.avance !== null ? ` (va ${tasaTexto(c.progreso.avance)})` : ""}</>
          )}
        </p>
        {c.bajaEnUnClic === false && !alertaBaja && (
          <p className="text-xs text-slate-500" role="note">
            Esta campaña no lleva la baja en un clic: quien no quiere recibir más lo pide contestando, y Snov no lo registra como baja.
            {equipo ? " Se prende en Snov, en la configuración del envío." : ""}
          </p>
        )}
        {r.error && <ErrorRefresco error={r.error} onReintentar={r.recargar} />}
      </header>

      <AvisoBajas pendientes={c.bajasPendientes} pidieron={k?.pidieronBaja || 0} campana={c.id} onListo={r.recargar}
        rutaPersonas={`${rutaCampana}?vista=personas&filtro=pidieron_baja`} />
      <Alertas alertas={c.alertas} />

      <Pestanas vista={vista} onElegir={irA} />

      <div id={`panel-email-${vista}`} role="tabpanel" aria-labelledby={`tab-email-${vista}`} tabIndex={0} className="focus:outline-none">
        {vista === "personas" ? (
          <TablaPersonas campanaId={c.id} />
        ) : vista === "respuestas" ? (
          <ListaRespuestas campanaId={c.id} />
        ) : !conDatos(k) ? (
          <Vacio>{c.estado === "borrador" ? "Es un borrador: todavía no le mandó a nadie." : "Todavía no hay envíos de esta campaña."}</Vacio>
        ) : (
          <div className="space-y-6">
            {seguimiento && <p className="text-xs text-slate-500" role="note">{seguimiento}</p>}
            <Indicadores conteos={k} tasas={c.tasas!} />
            <div className="grid gap-6 lg:grid-cols-5">
              <Tarjeta titulo="Del envío a la respuesta" className="lg:col-span-2">
                <Embudo conteos={k} />
              </Tarjeta>
              <Tarjeta titulo="Por día" sub="Desde que salió" className="lg:col-span-3">
                <GraficoDiario serie={c.diario} sinAperturas={k.abrieron === null} />
              </Tarjeta>
            </div>
            <div className="grid gap-6 lg:grid-cols-2">
              <Tarjeta titulo="Por asunto" sub="Cada paso de la secuencia (o cada variante)">
                {c.porAsunto.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        <tr>
                          <th className="py-2 pr-3">Asunto</th>
                          <th className="py-2 px-2 text-right">Enviados</th>
                          {k.abrieron !== null && <th className="py-2 px-2 text-right">Abrieron</th>}
                          {k.hicieronClic !== null && <th className="py-2 px-2 text-right">Clic</th>}
                          <th className="py-2 pl-2 text-right">Resp.</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {c.porAsunto.map((a) => (
                          <tr key={a.asunto}>
                            <td className="py-2 pr-3 text-slate-800 break-words">{asuntoLegible(a.asunto)}</td>
                            <td className="py-2 px-2 text-right tabular-nums">{num(a.enviados)}</td>
                            {k.abrieron !== null && <td className="py-2 px-2 text-right tabular-nums">{num(a.abrieron)}</td>}
                            {k.hicieronClic !== null && <td className="py-2 px-2 text-right tabular-nums">{num(a.hicieronClic)}</td>}
                            <td className="py-2 pl-2 text-right tabular-nums">{num(a.respondieron)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="mt-2 text-[11px] text-slate-400">Personas que abrieron, hicieron clic o contestaron ese mail. «‹nombre›» es la variable que Snov reemplaza por el nombre de cada uno.</p>
                  </div>
                ) : <Vacio>Sin asuntos para mostrar.</Vacio>}
              </Tarjeta>
              <Tarjeta titulo="A qué hora interactúan" sub="Aperturas, clics y respuestas por hora del día">
                <GraficoHoras porHora={c.porHora} sinAperturas={k.abrieron === null} />
              </Tarjeta>
            </div>
            {c.respuestasRecientes.length > 0 && (
              <Tarjeta titulo="Últimas respuestas" right={<button type="button" onClick={() => irA("respuestas")} className="text-sm font-semibold text-violet-700 hover:underline">Ver todas</button>}>
                <ul className="divide-y divide-slate-100">
                  {c.respuestasRecientes.map((x) => (
                    <li key={x.id} className="py-3 first:pt-0 last:pb-0">
                      <p className="text-sm">
                        <span className="font-semibold text-slate-900">{x.nombre || "Sin nombre"}</span> <span className="text-xs text-slate-500 break-all">{x.email}</span> <span className="text-xs text-slate-400">· {diaMesHora(x.recibidaEn)}</span>
                        {x.pideBaja && <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-50 text-red-700">Pide la baja</span>}
                      </p>
                      <p className="mt-1 text-sm text-slate-700 line-clamp-2 whitespace-pre-wrap break-words">{x.texto || "(sin texto)"}</p>
                    </li>
                  ))}
                </ul>
              </Tarjeta>
            )}
            {/* sólo esta campaña, con todo lo que pasó desde que salió: el rango no aplica */}
            <AnalisisEmails rango={{}} campana={c.id} />
          </div>
        )}
      </div>
    </div>
  );
}
